/**
 * Admin Sidebar Navigation
 *
 * Distinct from customer sidebar - internal tooling look.
 */

import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Building2,
  ClipboardCheck,
  ScrollText,
  Settings,
  ChevronLeft,
  ChevronRight,
  Shield,
  LogOut,
  MessageSquareWarning,
  FileSearch,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface NavItem {
  path: string;
  icon: React.ElementType;
  label: string;
  requiredRole?: 'superadmin' | 'admin' | 'support' | 'dev';
}

const NAV_ITEMS: NavItem[] = [
  { path: '/admin', icon: LayoutDashboard, label: 'Overview' },
  { path: '/admin/orgs', icon: Building2, label: 'Organizations' },
  { path: '/admin/runs', icon: ClipboardCheck, label: 'Compliance Runs' },
  { path: '/admin/moderation', icon: MessageSquareWarning, label: 'Moderation' },
  { path: '/admin/form-analyzer', icon: FileSearch, label: 'Form Analyzer' },
  { path: '/admin/audit', icon: ScrollText, label: 'Audit Log', requiredRole: 'superadmin' },
];

export function AdminSidebar() {
  const location = useLocation();
  const { adminUser, isSuperadmin, adminRole } = useAdminAuth();
  const { isInSupportMode, actingOrg } = useSupportMode();
  const [collapsed, setCollapsed] = useState(false);

  // Filter nav items based on role
  const visibleItems = NAV_ITEMS.filter((item) => {
    if (!item.requiredRole) return true;
    if (isSuperadmin) return true;
    if (item.requiredRole === 'superadmin') return false;
    // Add more role checks as needed
    return true;
  });

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen bg-[hsl(220,20%,10%)] border-r border-[hsl(220,15%,20%)] transition-all duration-200',
          // Account for warning banner at top (~36px)
          'pt-10',
          collapsed ? 'w-16' : 'w-56'
        )}
      >
        {/* Header */}
        <div className="flex h-14 items-center justify-between border-b border-[hsl(220,15%,20%)] px-3">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-500" />
              <span className="font-semibold text-white text-sm">Admin Console</span>
            </div>
          )}
          {collapsed && (
            <Shield className="h-5 w-5 text-amber-500 mx-auto" />
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              'h-7 w-7 text-gray-400 hover:text-white hover:bg-[hsl(220,15%,20%)]',
              collapsed && 'absolute -right-3 bg-[hsl(220,20%,10%)] border border-[hsl(220,15%,20%)]'
            )}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Support Mode Indicator */}
        {isInSupportMode && actingOrg && (
          <div
            className={cn(
              'mx-2 mt-2 rounded-md bg-amber-500/20 border border-amber-500/40 p-2',
              collapsed && 'p-1.5'
            )}
          >
            {!collapsed ? (
              <div className="text-xs">
                <p className="text-amber-400 font-medium">Support Mode</p>
                <p className="text-amber-300/80 truncate">{actingOrg.name || 'Organization'}</p>
              </div>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-full flex justify-center">
                    <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Support Mode: {actingOrg.name || 'Organization'}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="mt-4 px-2 space-y-1">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            const linkContent = (
              <NavLink
                to={item.path}
                end={item.path === '/admin'}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  active
                    ? 'bg-[hsl(220,70%,50%)] text-white'
                    : 'text-gray-400 hover:bg-[hsl(220,15%,15%)] hover:text-white',
                  collapsed && 'justify-center px-2'
                )}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );

            if (collapsed) {
              return (
                <Tooltip key={item.path}>
                  <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                  <TooltipContent side="right">
                    <p>{item.label}</p>
                  </TooltipContent>
                </Tooltip>
              );
            }

            return <div key={item.path}>{linkContent}</div>;
          })}
        </nav>

        {/* Footer - User info */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-[hsl(220,15%,20%)] p-3">
          {!collapsed ? (
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-[hsl(220,15%,25%)] flex items-center justify-center">
                <span className="text-xs text-white font-medium">
                  {adminUser?.email?.charAt(0).toUpperCase() || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-white truncate">
                  {adminUser?.displayName || adminUser?.email}
                </p>
                <p className="text-xs text-gray-500 capitalize">{adminRole}</p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href="/"
                    className="text-gray-400 hover:text-white"
                  >
                    <LogOut className="h-4 w-4" />
                  </a>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Exit Admin</p>
                </TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-center">
                  <div className="h-8 w-8 rounded-full bg-[hsl(220,15%,25%)] flex items-center justify-center">
                    <span className="text-xs text-white font-medium">
                      {adminUser?.email?.charAt(0).toUpperCase() || 'A'}
                    </span>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{adminUser?.email}</p>
                <p className="text-gray-400 capitalize">{adminRole}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </aside>
    </TooltipProvider>
  );
}
